import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayCircle,
  FileText,
  HelpCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Award,
  Download,
  AlertCircle,
  ArrowRight,
  BookOpen,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Course, CourseModule, Lesson, Assessment, AssessmentQuestion } from '../../types/database';
import Navbar from '../../components/feature/Navbar';

export default function LearnPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<(CourseModule & { lessons: Lesson[] })[]>([]);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [completedLessonIds, setCompletedLessonIds] = useState<string[]>([]);
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizAssessment, setQuizAssessment] = useState<Assessment | null>(null);
  const [quizQuestions, setQuizQuestions] = useState<AssessmentQuestion[]>([]);
  const [userAnswers, setUserAnswers] = useState<{ [qId: string]: string }>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState<number | null>(null);
  const [certificateCode, setCertificateCode] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (slug) fetchLearnData();
  }, [slug, user]);

  const fetchLearnData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Course
      const { data: crs, error: crsErr } = await supabase
        .from('courses')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (crsErr) {
        console.error('[LEARN] Error fetching course:', crsErr);
        setError('Failed to load course information.');
        return;
      }

      if (!crs) {
        setError('Course not found.');
        return;
      }
      setCourse(crs as Course);

      // 2. Fetch or verify enrollment
      let enrId: string | null = null;
      const { data: enr, error: enrErr } = await supabase
        .from('enrollments')
        .select('*')
        .eq('user_id', user!.id)
        .eq('course_id', crs.id)
        .maybeSingle();

      if (enrErr) {
        console.error('[LEARN] Error checking enrollment:', enrErr);
      }

      if (enr) {
        enrId = enr.id;
        setEnrollmentId(enr.id);
        setProgressPercent(enr.progress_percent || 0);
      } else {
        // If not enrolled but course is free, enroll automatically
        if (crs.price === 0) {
          const { data: newEnr, error: createEnrErr } = await supabase
            .from('enrollments')
            .insert({ user_id: user!.id, course_id: crs.id, status: 'Active' })
            .select()
            .maybeSingle();

          if (createEnrErr) {
            console.error('[LEARN] Auto-enrollment error:', createEnrErr);
          }
          if (newEnr) {
            enrId = newEnr.id;
            setEnrollmentId(newEnr.id);
          }
        } else {
          // Redirect to course page to enroll
          navigate(`/academy/courses/${slug}`);
          return;
        }
      }

      // 3. Fetch Modules & Lessons
      const { data: mods, error: modsErr } = await supabase
        .from('course_modules')
        .select('*, lessons(*)')
        .eq('course_id', crs.id)
        .order('order_index');

      if (modsErr) {
        console.error('[LEARN] Error fetching modules:', modsErr);
      }

      if (mods) {
        const sorted = mods.map((m: any) => ({
          ...m,
          lessons: (m.lessons || []).sort((a: any, b: any) => a.order_index - b.order_index),
        }));
        setModules(sorted);

        // Flatten lessons
        const allLessons: Lesson[] = sorted.flatMap((m) => m.lessons);
        if (allLessons.length > 0) {
          setCurrentLesson(allLessons[0]);
        }
      }

      // 4. Fetch Progress
      if (enrId) {
        const { data: progress, error: progErr } = await supabase
          .from('lesson_progress')
          .select('lesson_id, is_completed')
          .eq('enrollment_id', enrId)
          .eq('is_completed', true);

        if (progErr) console.error('[LEARN] Error fetching lesson progress:', progErr);
        if (progress) {
          setCompletedLessonIds(progress.map((p) => p.lesson_id));
        }

        // 5. Check if certificate already issued
        const { data: cert, error: certErr } = await supabase
          .from('certificates')
          .select('certificate_code')
          .eq('user_id', user!.id)
          .eq('course_id', crs.id)
          .maybeSingle();

        if (certErr) console.error('[LEARN] Error checking certificate:', certErr);
        if (cert) {
          setCertificateCode(cert.certificate_code);
        }
      }

      // 6. Fetch Quiz for this course if available
      const { data: assessment, error: assessErr } = await supabase
        .from('assessments')
        .select('*')
        .eq('course_id', crs.id)
        .maybeSingle();

      if (assessErr) console.error('[LEARN] Error checking assessment:', assessErr);
      if (assessment) {
        setQuizAssessment(assessment as Assessment);
        const { data: questions, error: qErr } = await supabase
          .from('assessment_questions')
          .select('*')
          .eq('assessment_id', assessment.id)
          .order('order_index');
        if (qErr) console.error('[LEARN] Error fetching assessment questions:', qErr);
        if (questions) setQuizQuestions(questions as AssessmentQuestion[]);
      }
    } catch (err) {
      console.error('[LEARN] Error fetching learn data:', err);
      setError('An unexpected error occurred while loading course material.');
    } finally {
      setLoading(false);
    }
  };

  const markLessonComplete = async (lessonId: string) => {
    if (!enrollmentId || !course) return;

    try {
      // Upsert into lesson_progress
      await supabase.from('lesson_progress').upsert(
        {
          enrollment_id: enrollmentId,
          lesson_id: lessonId,
          is_completed: true,
          completed_at: new Date().toISOString(),
        },
        { onConflict: 'enrollment_id, lesson_id' }
      );

      const allLessons = modules.flatMap((m) => m.lessons);
      const newCompleted = Array.from(new Set([...completedLessonIds, lessonId]));
      setCompletedLessonIds(newCompleted);

      const newPercent = Math.min(100, Math.round((newCompleted.length / allLessons.length) * 100));
      setProgressPercent(newPercent);

      await supabase
        .from('enrollments')
        .update({
          progress_percent: newPercent,
          completed_at: newPercent === 100 ? new Date().toISOString() : null,
          status: newPercent === 100 ? 'Completed' : 'Active',
        })
        .eq('id', enrollmentId);

      // Auto-issue certificate if 100% completed and certificate not yet created
      if (newPercent === 100 && !certificateCode) {
        issueCertificate();
      }

      // Jump to next lesson
      const currentIdx = allLessons.findIndex((l) => l.id === lessonId);
      if (currentIdx !== -1 && currentIdx < allLessons.length - 1) {
        setCurrentLesson(allLessons[currentIdx + 1]);
      }
    } catch (err) {
      console.error('Error marking lesson complete:', err);
    }
  };

  const issueCertificate = async () => {
    if (!user || !course) return;

    try {
      const code = `BM-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      const recipientName = profile?.full_name || user.email || 'Student';

      const { data: cert, error } = await supabase
        .from('certificates')
        .insert({
          certificate_code: code,
          user_id: user.id,
          course_id: course.id,
          recipient_name: recipientName,
          course_title: course.title,
          instructor_name: 'Bienvenu Masengesho',
          issue_date: new Date().toISOString().split('T')[0],
          grade: 'Distinction',
          is_valid: true,
        })
        .select()
        .single();

      if (!error && cert) {
        setCertificateCode(cert.certificate_code);
        setShowCelebration(true);
      }
    } catch (err) {
      console.error('Certificate generation error:', err);
    }
  };

  const handleQuizSubmit = () => {
    if (quizQuestions.length === 0) return;

    let correctCount = 0;
    quizQuestions.forEach((q) => {
      if (userAnswers[q.id] === q.correct_answer) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / quizQuestions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);

    if (score >= (quizAssessment?.passing_score_percent || 75)) {
      if (!certificateCode) {
        issueCertificate();
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-medium">Opening Course Classroom...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-6">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-900/50 text-red-400 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold mb-2">Unable to Load Classroom</h2>
          <p className="text-sm text-gray-400 mb-6">{error || 'The requested course could not be found.'}</p>
          <div className="flex justify-center gap-3">
            <button
              onClick={fetchLearnData}
              className="px-5 py-2.5 bg-clairBlue-600 hover:bg-clairBlue-500 text-white rounded-xl text-xs font-bold transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/academy"
              className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-semibold transition-colors"
            >
              Back to Academy
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const allLessons = modules.flatMap((m) => m.lessons);

  return (
    <div className="min-h-screen flex flex-col bg-gray-950 text-gray-100">
      {/* Top Classroom Bar */}
      <header className="h-16 bg-gray-900 border-b border-gray-800 px-6 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <Link
            to="/academy"
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Catalog</span>
          </Link>
          <div className="h-4 w-px bg-gray-700 hidden sm:block" />
          <h1 className="text-sm font-bold text-white truncate max-w-md">
            {course?.title}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Progress bar */}
          <div className="hidden md:flex items-center gap-3">
            <span className="text-xs text-gray-400 font-semibold">{progressPercent}% complete</span>
            <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-clairBlue-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {certificateCode && (
            <Link
              to={`/verify-certificate/${certificateCode}`}
              className="px-3 py-1.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-amber-500/30 transition-colors"
            >
              <Award className="w-4 h-4 text-amber-400" />
              <span>Certificate</span>
            </Link>
          )}

          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded-xl text-xs font-semibold hover:bg-gray-700 transition-colors"
          >
            {sidebarOpen ? 'Hide Lessons' : 'Show Lessons'}
          </button>
        </div>
      </header>

      {/* Main Classroom Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Content Viewer (Center) */}
        <div className="flex-1 flex flex-col overflow-y-auto bg-gray-900">
          <div className="max-w-4xl w-full mx-auto p-6 sm:p-10 flex-1 flex flex-col justify-between">
            {currentLesson ? (
              <div>
                {/* Lesson Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-xs text-clairBlue-400 font-semibold uppercase tracking-wider mb-2">
                    <span>Lesson Content</span>
                    <span>•</span>
                    <span>{currentLesson.duration_minutes} min read/watch</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                    {currentLesson.title}
                  </h2>
                </div>

                {/* Lesson Media: Video Player */}
                {currentLesson.content_type === 'video' && currentLesson.video_url && (
                  <div className="aspect-video w-full rounded-3xl overflow-hidden bg-black mb-8 shadow-2xl border border-gray-800">
                    <iframe
                      src={currentLesson.video_url}
                      title={currentLesson.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* Lesson Text Content */}
                <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed space-y-4 mb-8">
                  {currentLesson.content_text ? (
                    <p className="whitespace-pre-line text-sm sm:text-base">
                      {currentLesson.content_text}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">
                      Watch the high-definition video walkthrough above, follow the on-screen instructions, and practice the demonstrated workflow in your local workspace.
                    </p>
                  )}
                </div>

                {/* Quiz Assessment Section */}
                {quizQuestions.length > 0 && (
                  <div className="mt-10 p-8 bg-gray-800/60 rounded-3xl border border-gray-700">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <HelpCircle className="w-6 h-6 text-amber-400" />
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            {quizAssessment?.title || 'Knowledge Assessment'}
                          </h3>
                          <p className="text-xs text-gray-400">
                            Passing score: {quizAssessment?.passing_score_percent || 75}%
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {quizQuestions.map((q, idx) => (
                        <div key={q.id} className="p-4 bg-gray-900/80 rounded-2xl border border-gray-700/60">
                          <p className="text-sm font-semibold text-white mb-3">
                            {idx + 1}. {q.question_text}
                          </p>
                          <div className="space-y-2">
                            {q.options.map((opt, oIdx) => (
                              <label
                                key={oIdx}
                                className={`flex items-center gap-3 p-3 rounded-xl border text-xs cursor-pointer transition-colors ${
                                  userAnswers[q.id] === opt
                                    ? 'bg-clairBlue-500/20 border-clairBlue-500 text-white'
                                    : 'bg-gray-800/40 border-gray-700 text-gray-300 hover:bg-gray-800'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`question_${q.id}`}
                                  value={opt}
                                  checked={userAnswers[q.id] === opt}
                                  onChange={() => setUserAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                                  className="text-clairBlue-500 focus:ring-0"
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}

                      {quizSubmitted && quizScore !== null && (
                        <div
                          className={`p-4 rounded-2xl border text-sm font-semibold flex items-center justify-between ${
                            quizScore >= (quizAssessment?.passing_score_percent || 75)
                              ? 'bg-emerald-950/60 border-emerald-500 text-emerald-300'
                              : 'bg-red-950/60 border-red-500 text-red-300'
                          }`}
                        >
                          <div>
                            <p className="font-bold text-base">Your Score: {quizScore}%</p>
                            <p className="text-xs opacity-90">
                              {quizScore >= (quizAssessment?.passing_score_percent || 75)
                                ? 'Congratulations! You passed the assessment!'
                                : 'You did not reach the 75% passing threshold. Try reviewing the lessons and retake the quiz.'}
                            </p>
                          </div>
                          {quizScore < (quizAssessment?.passing_score_percent || 75) && (
                            <button
                              onClick={() => {
                                setQuizSubmitted(false);
                                setUserAnswers({});
                              }}
                              className="px-3 py-1.5 bg-red-800 text-white rounded-lg text-xs hover:bg-red-700"
                            >
                              Retake Quiz
                            </button>
                          )}
                        </div>
                      )}

                      {!quizSubmitted && (
                        <button
                          onClick={handleQuizSubmit}
                          disabled={Object.keys(userAnswers).length < quizQuestions.length}
                          className="px-6 py-3 bg-gradient-to-r from-clairBlue-600 to-darkBlue-600 text-white rounded-xl text-xs font-bold hover:shadow-lg transition-all disabled:opacity-50"
                        >
                          Submit Assessment Answers
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-20 text-gray-500">
                Select a lesson from the curriculum sidebar to begin.
              </div>
            )}

            {/* Bottom Actions Bar */}
            {currentLesson && (
              <div className="mt-12 pt-6 border-t border-gray-800 flex items-center justify-between gap-4">
                <button
                  onClick={() => {
                    const idx = allLessons.findIndex((l) => l.id === currentLesson.id);
                    if (idx > 0) setCurrentLesson(allLessons[idx - 1]);
                  }}
                  disabled={allLessons.findIndex((l) => l.id === currentLesson.id) === 0}
                  className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <button
                  onClick={() => markLessonComplete(currentLesson.id)}
                  className={`px-6 py-3 rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg transition-all ${
                    completedLessonIds.includes(currentLesson.id)
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                      : 'bg-gradient-to-r from-clairBlue-500 to-darkBlue-600 hover:scale-105 text-white'
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    {completedLessonIds.includes(currentLesson.id)
                      ? 'Completed • Continue Next'
                      : 'Mark Complete & Continue'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: Curriculum Modules & Lessons */}
        {sidebarOpen && (
          <aside className="w-80 sm:w-96 bg-gray-900 border-l border-gray-800 flex flex-col overflow-y-auto">
            <div className="p-4 border-b border-gray-800">
              <h3 className="font-bold text-sm text-white flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-clairBlue-400" />
                <span>Course Content</span>
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {completedLessonIds.length} of {allLessons.length} lessons completed
              </p>
            </div>

            <div className="divide-y divide-gray-800/60 flex-1">
              {modules.map((mod) => (
                <div key={mod.id} className="p-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 px-3 py-2">
                    {mod.title}
                  </h4>
                  <div className="space-y-1 mt-1">
                    {(mod.lessons || []).map((lesson) => {
                      const isActive = currentLesson?.id === lesson.id;
                      const isDone = completedLessonIds.includes(lesson.id);

                      return (
                        <button
                          key={lesson.id}
                          onClick={() => setCurrentLesson(lesson)}
                          className={`w-full text-left p-3 rounded-xl text-xs flex items-center justify-between gap-3 transition-colors ${
                            isActive
                              ? 'bg-clairBlue-600 text-white font-bold'
                              : 'text-gray-300 hover:bg-gray-800'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            {isDone ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                            ) : lesson.content_type === 'video' ? (
                              <PlayCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="truncate">{lesson.title}</span>
                          </div>
                          <span className="text-[11px] opacity-70 flex-shrink-0">
                            {lesson.duration_minutes}m
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>

      {/* Course Completion Celebration Modal */}
      <AnimatePresence>
        {showCelebration && certificateCode && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-gray-900 border border-amber-500/40 rounded-3xl p-8 max-w-lg w-full text-center relative overflow-hidden"
            >
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-amber-400 to-amber-600 text-white mx-auto flex items-center justify-center mb-4 shadow-xl">
                <Award className="w-10 h-10" />
              </div>

              <h3 className="text-2xl font-black text-white mb-2">
                Congratulations, Graduate! 🎓
              </h3>
              <p className="text-sm text-gray-300 mb-6 leading-relaxed">
                You have successfully completed <strong>{course?.title}</strong> and met all assessment standards. Your official certificate has been issued!
              </p>

              <div className="p-4 bg-gray-800/80 rounded-2xl border border-gray-700 mb-6 text-left text-xs space-y-1">
                <p className="text-gray-400">Certificate Verification Identifier:</p>
                <p className="text-amber-400 font-mono font-bold text-sm">{certificateCode}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/verify-certificate/${certificateCode}`}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl text-xs hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Award className="w-4 h-4" />
                  <span>View Official Certificate</span>
                </Link>
                <button
                  onClick={() => setShowCelebration(false)}
                  className="py-3 px-6 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-semibold"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
